'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import type * as React from 'react';
import type { JSX } from 'react';
import AnimatedLoadingIcon from '@/icons/AnimatedLoadingIcon';
import { cn } from '@/utils/ui-utils';

const inputUnit = cva('', {
  variants: {
    variant: {
      default: 'hidden',
      unit: 'text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const inputSpinner = cva('', {
  variants: {
    loading: {
      hidden: 'hidden',
      show: 'absolute top-0 left-3 flex items-center w-full h-full opacity-50',
    },
  },
  defaultVariants: {
    loading: 'hidden',
  },
});

type UnitType<T, U extends string = string> = T extends 'default'
  ? object
  : { unitLabel?: U; icon?: JSX.Element; command?: string; showClearButton?: boolean };
export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputUnit>,
    VariantProps<typeof inputSpinner>,
    UnitType<typeof inputUnit> {}

const Input = ({
  className,
  variant = 'default',
  loading = 'hidden',
  unitLabel = '',
  type,
  icon,
  command,
  showClearButton = false,
  ...props
}: InputProps & React.ComponentProps<'input'>) => {
  const handleClear = () => {
    props.onChange?.({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>);
  };
  return (
    <div className={cn('relative mt-3', className)}>
      <input
        type={type}
        data-icon={!!icon}
        data-command={!!command}
        data-clear={!!showClearButton}
        tabIndex={0}
        className={cn(
          `border-input placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full items-center
          rounded-sm border p-3 text-base transition-colors file:border-0 file:bg-transparent file:text-sm
          file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden
          disabled:cursor-not-allowed disabled:opacity-50`,
          'data-[icon=true]:pl-9',
          showClearButton && command && 'truncate pr-13',
          !showClearButton && command && 'pr-10',
          className,
        )}
        value={loading === 'hidden' ? props.value : ''}
        {...props}
      />

      <span
        className={cn(
          inputSpinner({
            loading,
          }),
        )}
      >
        <AnimatedLoadingIcon className='h-4 w-4' />
      </span>
      {!!icon && loading !== 'show' && (
        <span className='pointer-events-none absolute top-1/2 left-3 -translate-y-1/2'>{icon}</span>
      )}
      <span
        className={cn(
          inputUnit({
            variant,
          }),
        )}
      >
        {unitLabel}
      </span>
      {showClearButton && props.value && props.value !== '' && !props.disabled && (
        <button
          type='button'
          onClick={handleClear}
          onMouseDown={(event) => event.preventDefault()}
          title='Töm sökfältet'
          className='text-muted-foreground hover:bg-muted hover:text-accent-foreground absolute top-1/2 right-7 flex h-6
            w-6 -translate-y-1/2 items-center justify-center rounded-sm transition-colors'
          aria-label='Töm sökfältet'
        >
          <X className='h-4 w-4 text-[10px]' />
        </button>
      )}
      {!!command && (
        <span
          className={`bg-muted pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 rounded-sm px-1.5 py-0.5
          text-[10px] ${props.disabled ? 'opacity-50' : ''}`}
        >
          {command}
        </span>
      )}
    </div>
  );
};
Input.displayName = 'Input';

export { Input };
